/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {startTransition, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'
// Components
import {
    Box,
    Button,
    Flex,
    useBreakpointValue,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'
import {
    BR_SEARCH_URL,
    BR_SORT_OPTIONS,
    DEFAULT_SEARCH_PARAMS,
    SELECTED_ATTRIBUTES_PLP_PRODUCTS
} from '../../constants'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {useGlobaleContext} from '../../contexts/globale'
import useTagManager from '../../hooks/use-tag-manager'
import {formatCategoryTree} from '../../utils/product-utils'
import {formatDate} from '../../utils/product-utils'
import {buildApiUrl, getOrCreateBrUid2, yieldToMain} from '../../utils/common-utils'
import LoadingSpinner from '../loading-spinner'
import useCustomerGroups from '../../hooks/use-customer-groups'
import {
    createProductListAnalyticsTasks,
    scheduleAnalyticsTasks
} from '../../pages/product-list/analytics'
import {useSiteContext} from '../../contexts/site'

const InfiniteScroll = (props) => {
    const selectedAttributes = SELECTED_ATTRIBUTES_PLP_PRODUCTS
    const {isYR} = useSiteContext()
    const intl = useIntl()
    const appOrigin = getAppOrigin()
    const {
        searchParams,
        totalItems,
        setHits,
        hitsLength,
        noOfTiles,
        setTilesSkeletonCount,
        isGridLoading,
        category,
        triggerMonetateEvent,
        bloomReachParams,
        previousHits = []
    } = props
    const [showLoader, setShowLoader] = useState(false)
    const [showSpinner, setShowSpinner] = useState(false)
    if (category) {
        category.id = category.c_id
        category.name = category.c_name
        category.parentCategoryTree = category.c_parentCategoryTree
    }
    var breadCrumb = category?.parentCategoryTree
        ? formatCategoryTree(category.parentCategoryTree)
        : ''
    const infiniteScrollStyles = useStyleConfig('InfiniteScrollStyles')
    const productListStyles = useStyleConfig('ProductListStyles')
    const isMobile = useBreakpointValue({base: true, md: false})
    const {getTokenWhenReady} = useAccessToken()
    const api = useCommerceApi()
    const location = useLocation()
    const tagManager = useTagManager()
    const analyticsContextVersionRef = useRef(0)
    const {site} = useMultiSite()
    const [viewMore, setViewMore] = useState(noOfTiles < totalItems)
    const {
        app: {commerceAPI}
    } = getConfig()
    let brSortingOptions = BR_SORT_OPTIONS
    const {bloomreach, storefront} = commerceAPI
    const {config} = bloomreach || {}
    const {hostUrl} = storefront || {}
    let enableBloomreachSearch = null,
        catalogViews = '',
        accountId = ''
    try {
        const configAsJson = JSON.parse(config)
        brSortingOptions = configAsJson.sortOptions
        enableBloomreachSearch = configAsJson.enableBloomreachSearch
        accountId = configAsJson.accountId
        catalogViews = configAsJson.catalogViews
    } catch (e) {
        console.log(e.message)
    }

    const cookie =
        onClient() &&
        new CookieStorage({
            keySuffix: '',
            sharedContext: !onClient()
        })
    const {countryISO, currencyCode} = useGlobaleContext()
    const {data: customerGroupIds} = useCustomerGroups()

    const getBRSearch = async (
        query,
        searchType,
        requestType,
        rows,
        fqParams,
        sort,
        categoryFilters,
        disableRules,
        formattedDate
    ) => {
        const sortValue = brSortingOptions?.find((o) => o.id === sort)?.value || ''
        const baseUrl = new URL(`${appOrigin}${BR_SEARCH_URL}`)

        const merchandisingRules = disableRules
            ? {'merchandising_rules.disable': 'attribute_include, attribute_exclude'}
            : {}

        const queryParams = {
            start: noOfTiles,
            rows: rows,
            account_id: accountId,
            domain_key: catalogViews,
            q: query,
            sort: sortValue,
            request_type: requestType,
            fl: 'pid,skuid,product_type',
            search_type: searchType,
            efq: categoryFilters,
            ref_url: hostUrl + location?.pathname + location?.search,
            url: hostUrl + location?.pathname + location?.search,
            _br_uid_2: getOrCreateBrUid2(cookie),
            fqParams: JSON.stringify(fqParams || {}),
            ...merchandisingRules
        }

        const url = buildApiUrl(baseUrl, queryParams)
        const apiResponse = await fetch(url, {
            credentials: 'omit',
            headers: {
                accept: 'application/json',
                formatteddate: formattedDate
            }
        })

        return apiResponse.json()
    }

    const currentSort = searchParams?.sort || category?.c_defaultSortingOption || ''

    const sortLabel =
        (brSortingOptions || [])?.find((o) => o.id === currentSort || o.value === currentSort)
            ?.label || ''

    const trackLoadedProducts = (products, analyticsContextVersion) => {
        if (analyticsContextVersion !== analyticsContextVersionRef.current) {
            return
        }

        const tasks = createProductListAnalyticsTasks({
            hits: products || [],
            breadcrumb: breadCrumb,
            category,
            sortLabel,
            isMobile,
            tagManager,
            startIndex: hitsLength
        })
        if (!tasks.length) {
            return
        }

        scheduleAnalyticsTasks(tasks).flush()
    }

    useEffect(
        () => () => {
            // Ignore View More responses that finish after the PLP context changes.
            analyticsContextVersionRef.current += 1
        },
        [location.pathname, location.search]
    )

    const appendHits = (newHits, skeletonCount, monetateHits) => {
        startTransition(() => {
            setHits((prevHits) => [...(prevHits || []), ...newHits])
            setTilesSkeletonCount(skeletonCount)
        })
        triggerMonetateEvent(monetateHits)
    }

    useEffect(() => {
        if (noOfTiles && totalItems) {
            /**
             * Using 'limitValue' to cater the casecase when user clicks the back button from the URL bar.
             * Using 'noOfTiles' to cater the casecase when user clicks View More button or refreshes the URL.
             * 'totalItems' is the one returned by API.
             */
            setViewMore(noOfTiles < totalItems)
        }
    }, [noOfTiles, totalItems])

    // Function to trigger API for infinite scroll.
    const infiniteScroll = async () => {
        if (isGridLoading || showLoader) {
            return
        }

        setShowSpinner(true)
        setShowLoader(true)
        const analyticsContextVersion = analyticsContextVersionRef.current
        await yieldToMain()
        const defaultLimit = DEFAULT_SEARCH_PARAMS.limit
        const currentParams = new URLSearchParams(window.location.search)

        const newSearchParams = {
            ...searchParams,
            limit:
                totalItems - noOfTiles < DEFAULT_SEARCH_PARAMS.limit
                    ? totalItems - noOfTiles
                    : DEFAULT_SEARCH_PARAMS.limit,
            offset: noOfTiles
        }
        setTilesSkeletonCount(newSearchParams.limit)
        if (props?.category?.id && !enableBloomreachSearch) {
            // Do not insert this for Bloomreach
            newSearchParams?._refine?.push(`cgid=${props?.category?.id}`)
        }
        const {_refine, ...restOfParams} = newSearchParams
        const token = await getTokenWhenReady()
        let hitsToShow = []
        try {
            const queryParams = {
                siteId: site?.id,
                ...restOfParams,
                perPricebook: true,
                allVariationProperties: true,
                allImages: true,
                expand: [
                    'promotions',
                    'variations',
                    'prices',
                    'images',
                    'page_meta_tags',
                    'custom_properties',
                    'represented_products',
                    'availability'
                ].join(','),
                refine: _refine,
                c_selectedCountry: countryISO,
                c_selectedCurrency: currencyCode
            }
            var tilesLength = 0
            var searchResults
            if (enableBloomreachSearch) {
                const {query, searchType, requestType, rows, effectiveDateTime} =
                    bloomReachParams || {}
                const formattedDate = formatDate(effectiveDateTime)
                const currentDate = formatDate()
                const disableRules = formattedDate > currentDate
                const fqParams = newSearchParams?._refine?.map((entry) => {
                    const [key, values] = entry.split('=')
                    const orValues = values
                        ?.split('|')
                        ?.map((v) => `"${v}"`)
                        ?.join(' OR ')
                    return `${key}:${orValues}`
                })
                const {response: brResponse} = await getBRSearch(
                    query || category?.c_brg_dyn_category || category?.c_id,
                    searchType,
                    requestType,
                    rows,
                    fqParams,
                    searchParams?.sort,
                    category?.c_brg_category_filters,
                    disableRules,
                    formattedDate
                )
                tilesLength = brResponse?.docs?.length
                const allDocs = brResponse?.docs || []
                const chunkSize = 12

                // ---- FIRST BATCH ----
                const firstBatch = allDocs.slice(0, chunkSize)
                if (firstBatch.length > 0) {
                    const firstIds = firstBatch
                        .map((d) => d?.variants?.[0]?.skuid || d.pid)
                        .filter(Boolean)
                        .join(',')
                    const firstResults = await api.shopperProducts.getProducts({
                        parameters: {
                            ids: firstIds,
                            allImages: true,
                            perPricebook: true,
                            select: selectedAttributes,
                            expand: [
                                'availability',
                                'links',
                                'promotions',
                                'options',
                                'images',
                                'prices',
                                'variations'
                            ],
                            c_selectedCountry: countryISO,
                            c_isBloomreachSearch: true,
                            c_selectedCurrency: currencyCode
                        },
                        headers: {Authorization: `Bearer ${token}`}
                    })

                    hitsToShow = firstResults?.data || []
                    hitsToShow = hitsToShow.filter((hit) => {
                        const showTile =
                            hit?.c_pdpRestrictionEnabled && hit?.c_pdpRestrictionGroups
                                ? hit?.c_pdpRestrictionGroups?.find(
                                      (group) =>
                                          customerGroupIds?.customerGroupIds.indexOf(group) > -1
                                  )
                                : true
                        return showTile
                    })

                    trackLoadedProducts(firstResults?.data, analyticsContextVersion)
                    // Save first batch immediately
                    appendHits(hitsToShow, Math.max(0, allDocs.length - firstBatch.length), [
                        ...previousHits,
                        ...hitsToShow
                    ])
                }

                // ---- SECOND BATCH (NON-BLOCKING) ----
                const secondBatch = allDocs.slice(chunkSize, chunkSize * 2)
                if (secondBatch.length > 0) {
                    ;(async () => {
                        try {
                            const secondIds = secondBatch
                                .map((d) => d?.variants?.[0]?.skuid || d.pid)
                                .filter(Boolean)
                                .join(',')
                            const secondResults = await api.shopperProducts.getProducts({
                                parameters: {
                                    ids: secondIds,
                                    allImages: true,
                                    perPricebook: true,
                                    select: selectedAttributes,
                                    expand: [
                                        'availability',
                                        'links',
                                        'promotions',
                                        'options',
                                        'images',
                                        'prices',
                                        'variations'
                                    ],
                                    c_selectedCountry: countryISO,
                                    c_isBloomreachSearch: true,
                                    c_selectedCurrency: currencyCode
                                },
                                headers: {Authorization: `Bearer ${token}`}
                            })

                            let hitsToShowSecond = secondResults?.data || []
                            hitsToShowSecond = hitsToShowSecond.filter((hit) => {
                                const showTile =
                                    hit?.c_pdpRestrictionEnabled && hit?.c_pdpRestrictionGroups
                                        ? hit?.c_pdpRestrictionGroups?.find(
                                              (group) =>
                                                  customerGroupIds?.customerGroupIds.indexOf(
                                                      group
                                                  ) > -1
                                          )
                                        : true
                                return showTile
                            })
                            trackLoadedProducts(secondResults?.data, analyticsContextVersion)
                            // Save second batch when ready
                            appendHits(hitsToShowSecond, 0, [
                                ...previousHits,
                                ...hitsToShow,
                                ...hitsToShowSecond
                            ])
                        } catch (error) {
                            console.error('Error fetching second batch of product results:', error)
                        } finally {
                            startTransition(() => setTilesSkeletonCount(0))
                        }
                    })()
                } else {
                    setTilesSkeletonCount(0)
                }
            } else {
                const results = await api.shopperSearch.productSearch({
                    parameters: queryParams,
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    keepPreviousData: true
                })
                tilesLength = results?.hits?.length
                trackLoadedProducts(results?.hits, analyticsContextVersion)
                searchResults = results?.hits
                searchResults = searchResults?.filter((hit) => {
                    const showTile =
                        hit?.c_pdpRestrictionEnabled && hit?.c_pdpRestrictionGroups
                            ? hit?.c_pdpRestrictionGroups?.find(
                                  (group) => customerGroupIds?.customerGroupIds.indexOf(group) > -1
                              )
                            : true

                    return showTile
                })

                appendHits(searchResults, 0, [...previousHits, ...searchResults])
            }

            const nextLimit =
                (parseInt(currentParams.get('limit')) || queryParams.limit) + defaultLimit <
                totalItems
                    ? queryParams.limit + defaultLimit
                    : totalItems // Do not exceed the maximum number of available items (returned by API).
            if (nextLimit === totalItems) {
                setViewMore(false)
            }
            currentParams.set('limit', noOfTiles + tilesLength) // Set the new 'limit'
            currentParams.set('offset', 0) // 'offset' will always be 0

            window.history.pushState(
                null,
                '',
                `${window.location.pathname}?${currentParams.toString()}`
            ) // Update the URL in the browser without reloading.
            setShowLoader(false)
            setShowSpinner(false)
        } catch (error) {
            setShowLoader(false)
            setShowSpinner(false)
            setTilesSkeletonCount(0)
            console.error('Error fetching product results:', error)
        }
    }

    return (
        <Box data-testid="sf-infinite-scroll" {...infiniteScrollStyles.wrapper}>
            {showSpinner && <LoadingSpinner position="fixed" top="auto" />}
            {viewMore && (
                <Flex
                    {...productListStyles.footer}
                    visibility={isGridLoading || showLoader ? 'hidden' : 'visible'}
                    pointerEvents={isGridLoading || showLoader ? 'none' : 'auto'}
                    aria-hidden={isGridLoading || showLoader}
                >
                    <Button
                        aria-label={intl.formatMessage({
                            id: 'plp_view_more',
                            defaultMessage: 'View More'
                        })}
                        isLoading={showLoader}
                        isDisabled={isGridLoading || showLoader}
                        onClick={infiniteScroll}
                        {...infiniteScrollStyles.button}
                        brand={isYR ? 'yr' : 'ks'}
                    >
                        {intl.formatMessage({
                            id: 'plp_view_more',
                            defaultMessage: 'View More'
                        })}
                    </Button>
                </Flex>
            )}
        </Box>
    )
}

InfiniteScroll.propTypes = {
    searchParams: PropTypes.object,
    category: PropTypes.object,
    previousHits: PropTypes.array,
    totalItems: PropTypes.number,
    noOfTiles: PropTypes.number,
    setTilesSkeletonCount: PropTypes.func,
    isGridLoading: PropTypes.bool,
    bloomReachParams: PropTypes.object,
    triggerMonetateEvent: PropTypes.func,
    hitsLength: PropTypes.any,
    setHits: PropTypes.func
}

export default InfiniteScroll
